/* eslint-disable react/jsx-no-constructed-context-values */
/* eslint-disable unicorn/no-array-reduce */
/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
/* eslint-disable unicorn/no-null */
import PageLoader from 'components/pageLoader'
import { parseISO } from 'date-fns'
import { useUserProfileContext } from 'lib/contexts/userProfile.context'
import { useLoadApplicationFeatures } from 'lib/services/applicationFeature.service'
import React, { useContext, useEffect, useState } from 'react'

export interface Feature {
	comingSoon: boolean
	deprecated: string | null
	description: string | null
	gerund: string
	key: string
	name: string
	properties: {
		max: number
	}
	role: string
	tierKey: string
	tierName: string
	tierRank: number
	isLicensed?: boolean
}

interface SubscriptionInfo {
	startDate: string | null
	expiryDate: string | null
	tier: string | null
}

interface FeatureContextValue {
	applicationFeatures: Feature[]
	licensedFeatures: Feature[]
	getFeatureByKey: (
		key: string,
		org?: { currentSubscriptionInfo?: SubscriptionInfo } | null
	) => Feature | undefined
	getFeatureByKeyAndTier: (key: string, tier?: string) => Feature | null
	getFeaturesByKeys: (keys: string[]) => Feature[]
	getNextTier: (role: string, tier: string) => Feature | undefined
	canUpgrade: boolean
	isTrial: boolean
	promptUserToUpgrade: (
		show: boolean,
		featureKey: string,
		tier?: string
	) => void
	showUpgradePrompt: { show: boolean; feature: Feature | null }
	isLoaded: boolean
}

export const FeatureContext = React.createContext<FeatureContextValue | null>(
	null
)

export const FeatureProvider: React.FC<{ children: React.ReactNode }> = ({
	children
}) => {
	const { userProfile } = useUserProfileContext()
	const [applicationFeatures, setApplicationFeatures] = useState<Feature[]>([])
	const [licensedFeatures, setLicensedFeatures] = useState<Feature[]>([])
	const [showUpgradePrompt, setShowUpgradePrompt] = useState<{
		show: boolean
		feature: Feature | null
	}>({
		show: false,
		feature: null
	})
	const [isTrial, setIsTrial] = useState<boolean>(false)
	const [canUpgrade, setCanUpgrade] = useState(false)
	const { data: featuresData, isLoading } = useLoadApplicationFeatures(
		userProfile?.id ?? '',
		!!userProfile?.id
	)

	useEffect(() => {
		if (featuresData && userProfile?.id) {
			const tierRank = userProfile.isPaidSubscriber
				? featuresData.find(
						(f: Feature) => f.tierKey === userProfile.licenseInfo?.tier
					)?.tierRank || 0
				: 0
			const features = featuresData.map((d: Feature) => ({
				...d,
				isLicensed: userProfile.isAdmin ? true : d.tierRank <= tierRank
			}))
			setApplicationFeatures(features)

			setLicensedFeatures(
				featuresData
					.filter(
						(f: Feature) =>
							f.key && (userProfile.isAdmin || f.tierRank <= tierRank)
					)
					.sort((c: Feature, n: Feature) => n.tierRank - c.tierRank)
					.reduce<Feature[]>((accumulator, current) => {
						if (
							accumulator.some(
								f => f.role === current.role && f.key === current.key
							)
						) {
							return accumulator
						}
						return [
							...accumulator,
							{
								...current,
								isLicensed: userProfile.isAdmin
									? true
									: current.tierRank <= tierRank
							}
						]
					}, [])
			)

			const trail =
				userProfile.licenseType?.toLowerCase().includes('trial') || false
			setIsTrial(trail)
			setCanUpgrade(
				userProfile.isAdmin ||
					trail ||
					features.some((f: Feature) => !f.isLicensed)
			)
		}
	}, [featuresData, userProfile])

	const getFeatureByKey = (
		key: string,
		org?: { currentSubscriptionInfo?: SubscriptionInfo } | null
	): Feature | undefined => {
		if (org?.currentSubscriptionInfo) {
			const now = new Date()
			if (
				org.currentSubscriptionInfo.startDate &&
				parseISO(org.currentSubscriptionInfo.startDate) <= now
			) {
				if (
					!org.currentSubscriptionInfo.expiryDate ||
					parseISO(org.currentSubscriptionInfo.expiryDate) >= now
				) {
					const subscriptionTierRank =
						applicationFeatures.find(
							f => f.tierKey === org.currentSubscriptionInfo?.tier
						)?.tierRank ?? 0
					return applicationFeatures
						.sort((c, n) => n.tierRank - c.tierRank)
						.find(f => f.key === key && f.tierRank <= subscriptionTierRank)
				}
			} else {
				return applicationFeatures.find(f => f.key === key && f.tierRank === 0)
			}
		}
		return licensedFeatures.find(f => f.key === key)
	}

	const getFeatureByKeyAndTier = (key: string, tier?: string): Feature | null =>
		applicationFeatures.find(
			f => f.key === key && (!tier || f.tierKey === tier)
		) ?? null

	const getFeaturesByKeys = (keys: string[]): Feature[] =>
		licensedFeatures.filter(f => keys.includes(f.key))

	const getNextTier = (role: string, tier: string): Feature | undefined => {
		const thisTierRank =
			applicationFeatures.find(f => f.tierKey === tier)?.tierRank ?? 0
		return applicationFeatures
			.filter(f => f.role === role && f.tierRank > thisTierRank)
			.sort((c, n) => c.tierRank - n.tierRank)
			.find(f => f.role === role)
	}

	const promptUserToUpgrade = (
		show: boolean,
		featureKey: string,
		tier?: string
	) => {
		setShowUpgradePrompt({
			show,
			feature: getFeatureByKeyAndTier(featureKey, tier) || null
		})
	}

	return (
		<FeatureContext.Provider
			value={{
				applicationFeatures,
				licensedFeatures,
				getFeatureByKey,
				getFeatureByKeyAndTier,
				getFeaturesByKeys,
				getNextTier,
				canUpgrade,
				isTrial,
				promptUserToUpgrade,
				showUpgradePrompt,
				isLoaded: !isLoading
			}}
		>
			{isLoading ? <PageLoader /> : children}
		</FeatureContext.Provider>
	)
}

export const useFeatureContext = (): FeatureContextValue => {
	const context = useContext(FeatureContext)
	if (!context) {
		throw new Error(
			'useFeatureContext must be used within a UserProfileContext'
		)
	}
	return context
}
